import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './CertificateVerifyScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const CertificateVerifyScreen = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [cert, setCert] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const resp = await fetch(`${API_URL}/collections/certificates/verify/${token}`);
        const data = await resp.json();
        if (data.verified) {
          setCert(data.certificate);
        } else {
          setError(data.error || 'Certificate not found');
        }
      } catch (e) {
        setError('Unable to verify certificate. Please try again later.');
      }
      setLoading(false);
    };
    fetchCertificate();
  }, [token]);

  const formatCurrency = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '—';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  if (loading) {
    return (
      <div className="cv-container">
        <div className="cv-loading">
          <div className="cv-spinner" />
          <p>Verifying certificate…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cv-container">
        <div className="cv-card cv-card-error">
          <div className="cv-error-icon">✕</div>
          <h1>Certificate Not Verified</h1>
          <p className="cv-error-msg">{error}</p>
          <p className="cv-error-help">
            This certificate may have been revoked, or the link may be invalid.
            If you believe this is an error, contact the certificate holder.
          </p>
          <Link to="/" className="cv-btn cv-btn-primary">Visit AmpNebula</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-container">
      <div className="cv-card">
        {/* Verification Badge */}
        <div className="cv-verified-badge">
          <div className="cv-badge-icon">✓</div>
          <div className="cv-badge-text">
            <span className="cv-badge-label">Verified Certificate</span>
            <span className="cv-badge-number">{cert.certificate_number}</span>
          </div>
        </div>

        {/* Amp Identity */}
        <div className="cv-amp-header">
          <h1 className="cv-amp-manufacturer">{cert.manufacturer_name}</h1>
          <h2 className="cv-amp-model">{cert.model}</h2>
          {cert.year_range && <span className="cv-amp-year">{cert.year_range}</span>}
        </div>

        {/* Valuation Summary */}
        <div className="cv-valuation">
          <div className="cv-val-label">AmpNebula Valuation™</div>
          <div className="cv-val-amount">{formatCurrency(cert.estimated_value)}</div>
          {cert.estimated_value_low && cert.estimated_value_high && (
            <div className="cv-val-range">
              Range: {formatCurrency(cert.estimated_value_low)} – {formatCurrency(cert.estimated_value_high)}
            </div>
          )}
          {cert.insurance_value && (
            <div className="cv-val-insurance">
              Insurance / Replacement: {formatCurrency(cert.insurance_value)}
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div className="cv-details">
          <div className="cv-detail-row">
            <span className="cv-detail-label">Condition</span>
            <span className="cv-detail-value">{cert.condition || '—'}</span>
          </div>
          <div className="cv-detail-row">
            <span className="cv-detail-label">Confidence</span>
            <span className="cv-detail-value">{cert.confidence || '—'}</span>
          </div>
          <div className="cv-detail-row">
            <span className="cv-detail-label">Category</span>
            <span className="cv-detail-value" style={{ textTransform: 'capitalize' }}>
              {cert.category ? cert.category.replace(/_/g, ' ') : '—'}
            </span>
          </div>
          <div className="cv-detail-row">
            <span className="cv-detail-label">Method</span>
            <span className="cv-detail-value">
              {cert.valuation_method === 'ai_estimate' ? 'AmpNebula AI™ + Google Search' : 'Manual Appraisal'}
            </span>
          </div>
          <div className="cv-detail-row">
            <span className="cv-detail-label">Issued</span>
            <span className="cv-detail-value">
              {new Date(cert.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
          <div className="cv-detail-row">
            <span className="cv-detail-label">Valued By</span>
            <span className="cv-detail-value">{cert.valued_by}</span>
          </div>
        </div>

        {/* Reasoning */}
        {cert.reasoning && (
          <div className="cv-reasoning">
            <h3>Valuation Analysis</h3>
            <p>{cert.reasoning.replace(/AmpNebula Valuation™\s*—?\s*/g, '')}</p>
          </div>
        )}

        {/* Footer */}
        <div className="cv-footer">
          <div className="cv-disclaimer">
            This valuation is an estimate based on AI analysis and publicly available market data at the time of issuance.
            It does not constitute a professional appraisal.
          </div>
          <div className="cv-branding">
            <Link to="/" className="cv-brand-link">
              <strong>AmpNebula</strong> — The Encyclopedic Home of Guitar Amplifiers
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateVerifyScreen;
